import { useCallback, useState } from 'react';

import {
  useCart,
  useModal,
  useMyStore,
  useNavigateTo,
  useProductEntryDataLayerValues,
  useTireWheelPackage,
  useTreadwellPreferences
} from '../hooks';

import { trackAction } from '../../utils/analytics/analytics';
import { clearLastPlpProduct } from '../../utils/back-to-results/back-to-results';
import { getNormalizedProductType } from '../../utils/product/product';
import { getTreadwellAnalyticsInfo } from '../../utils/treadwell/treadwell';

import {
  ADD_TO_CART_DRAWER,
  CHECK_FIT,
  CONFIRM_STORE,
  PACKAGE_COMPLETE
} from '../../constants/modals';
import { BUNDLE_TYPE } from '../../constants/products';

function useAddProductToCart(args) {
  const { productItem, onProductAdded } = args || {};
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const { addToCart, addProductSetToCart, addSetToCart } = useCart();
  const navigateTo = useNavigateTo();
  const { myStore, isMyStoreConfirmed } = useMyStore();
  const {
    addPrebuiltPackageToCart,
    incompletePackage,
    currentTireWheelPackageFlow,
    addProductToTireWheelPackage
  } = useTireWheelPackage();
  const { treadwellPreferences } = useTreadwellPreferences();
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { closeModal, openModal } = useModal();
  const { isTreadwellFlow } = useTreadwellPreferences();
  const shouldAddToPackage = Boolean(currentTireWheelPackageFlow);

  const confirmedAddProduct = useCallback(
    async (storeCode, myStoreProductItem = productItem) => {
      const {
        prebuiltPackage,
        product,
        productSet,
        productSetQuantity,
        quantity,
        rearQuantity,
        totalQuantity
      } = myStoreProductItem || {};
      const {
        code: productCode,
        rearProduct,
        recommendationData
      } = product || {};
      let modalType;
      let modalProps;

      const isFinishingPackage =
        incompletePackage &&
        getNormalizedProductType(incompletePackage?.product?.productType) !==
          getNormalizedProductType(myStoreProductItem?.product?.productType);

      const analyticsInfo = getTreadwellAnalyticsInfo({
        isByo: shouldAddToPackage,
        isPrebuilt: Boolean(prebuiltPackage),
        recommendationRank: recommendationData?.rank,
        treadwellPreferences
      });

      setIsAddingProduct(true);
      if (shouldAddToPackage) {
        // adding another product of the same type will overwrite the existing product
        if (isFinishingPackage) {
          modalType = PACKAGE_COMPLETE;
          modalProps = {
            entriesAdded: isFinishingPackage
              ? [incompletePackage, myStoreProductItem]
              : [myStoreProductItem],
            quantity,
            rearQuantity,
            shouldPromptCancelOnClose: true
          };
        }

        await addProductToTireWheelPackage(
          analyticsInfo,
          product,
          quantity,
          rearProduct,
          rearQuantity,
          storeCode
        );
        trackAction(
          'shop_package_add_to_package',
          getProductEntryDataLayerValues(product)
        );
      } else if (prebuiltPackage) {
        modalType = PACKAGE_COMPLETE;
        modalProps = {
          entriesAdded: [
            { product: prebuiltPackage.product1, quantity: 4 * quantity },
            { product: prebuiltPackage.product2, quantity: 4 * quantity }
          ],
          hideStatus: true,
          isPrebuilt: true
        };

        await addPrebuiltPackageToCart({
          analyticsInfo,
          prebuiltPackage,
          quantity,
          storeCode
        });
      } else {
        if (process.env.__FF_ADD_TO_CART_DRAWER__ === 'true') {
          modalType = ADD_TO_CART_DRAWER;
          modalProps = {
            product,
            productSet,
            productSetQuantity,
            quantity,
            rearProduct,
            rearQuantity,
            totalQuantity
          };
        } else {
          modalType = CHECK_FIT;
          modalProps = {
            product,
            productSet,
            productSetQuantity,
            quantity,
            rearProduct,
            rearQuantity,
            totalQuantity
          };
        }

        const isRecommended = Boolean(recommendationData);

        if (product && quantity && rearProduct && rearQuantity) {
          await addSetToCart(storeCode, {
            analyticsInfo: analyticsInfo,
            dryStoppingDistance: recommendationData?.stopping?.dryDistance,
            frontProductCode: productCode,
            frontQuantity: quantity,
            isRecommended: isTreadwellFlow ? isRecommended : false,
            rearProductCode: rearProduct?.code,
            rearQuantity: rearQuantity,
            recommendationRank: recommendationData?.rank,
            wetStoppingDistance: recommendationData?.stopping?.wetDistance
          });
        } else if (productSet && productSetQuantity) {
          const productSetEntries = productSet?.map((product, idx) => ({
            analyticsInfo,
            fitmentPosition: product?.fitmentPosition?.code,
            productCode: product?.code,
            quantity: productSetQuantity?.[idx]
          }));
          await addProductSetToCart(storeCode, productSetEntries);
        } else {
          const isAddingRearProductOnly =
            rearProduct && rearQuantity && !quantity;
          const productCodeToAdd = isAddingRearProductOnly
            ? rearProduct?.code
            : productCode;
          const quantityToAdd = isAddingRearProductOnly
            ? rearQuantity
            : quantity;

          await addToCart(storeCode, {
            analyticsInfo: analyticsInfo,
            dryStoppingDistance: recommendationData?.stopping?.dryDistance,
            fitmentPosition: product?.fitmentPosition?.code,
            isRecommended: isTreadwellFlow ? isRecommended : false,
            productCode: productCodeToAdd,
            quantity: quantityToAdd,
            recommendationRank: recommendationData?.rank,
            wetStoppingDistance: recommendationData?.stopping?.wetDistance
          });
        }
      }

      if (onProductAdded) {
        onProductAdded();
      } else if (shouldAddToPackage && !isFinishingPackage) {
        clearLastPlpProduct();
        navigateTo('#/package/step-one-complete');
      } else {
        openModal(modalType, modalProps);
      }
      setIsAddingProduct(false);
    },
    [
      addPrebuiltPackageToCart,
      addProductSetToCart,
      addProductToTireWheelPackage,
      addSetToCart,
      addToCart,
      getProductEntryDataLayerValues,
      incompletePackage,
      isTreadwellFlow,
      navigateTo,
      onProductAdded,
      openModal,
      productItem,
      shouldAddToPackage,
      treadwellPreferences
    ]
  );

  const promptStoreConfirmation = useCallback(async () => {
    if (!myStore?.code) {
      return;
    }

    openModal(CONFIRM_STORE, {
      onClose: () => {
        closeModal();
      },
      onConfirmStore: result => {
        const myStoreProductItem = JSON.parse(JSON.stringify(productItem));
        const { storeData, isSiteSwapping, newProductData, bundleType } =
          result || {};
        const { code: storeCode } = storeData || {};

        if (newProductData) {
          if (bundleType === BUNDLE_TYPE.TIRE_WHEEL_PACKAGE) {
            myStoreProductItem.prebuiltPackage = newProductData;
          } else {
            myStoreProductItem.product = newProductData;
          }
        }

        if (storeCode && !isSiteSwapping) {
          confirmedAddProduct(storeCode, myStoreProductItem);
        }
        closeModal();
      },
      productItem: productItem
    });
  }, [confirmedAddProduct, closeModal, myStore, openModal, productItem]);

  const addProduct = useCallback(
    (
      isStoreConfirmed = isMyStoreConfirmed,
      productItem,
      storeCode = myStore?.code
    ) => {
      if (!isStoreConfirmed) {
        promptStoreConfirmation();
      } else {
        confirmedAddProduct(storeCode, productItem);
      }
    },
    [confirmedAddProduct, isMyStoreConfirmed, myStore, promptStoreConfirmation]
  );

  return {
    addProduct,
    isAddingProduct
  };
}

export default useAddProductToCart;
